import { type AppRole, type OrganizationType } from '@craft/database';

import {
  type AuthenticatedUser,
  type AuthenticatedUserWithSelectedRole,
  type ClientAuthenticatedUser,
  type ClientAuthenticatedUserWithSelectedRole,
  type ClientOrganizationRole,
  type DistributedClientOrganizationRole,
  type DistributedOrganizationRole,
  type OrganizationRole,
} from 'connect-graphql/auth';
import {
  type AppRole as GraphQlAppRole,
  type OrganizationType as GraphQlOrganizationType,
} from 'connect-graphql/generated';

/**
 * Represents the various forms of the primary argument that a given {@link RoleAuthorizer}
 * or {@link AdminRoleAuthorizer} can take, which is common to all overloaded signatures of the
 * function.
 *
 * The specific form of this primary argument determines the type that the {@link RoleAuthorizer}
 * or {@link AdminRoleAuthorizer} typeguard will narrow to when the function is called.
 */
export type RoleAuthorizerPrimaryArg =
  | AppRole
  | AuthenticatedUser
  | ClientAuthenticatedUser
  | ClientOrganizationRole
  | DistributedClientOrganizationRole
  | DistributedOrganizationRole
  | GraphQlAppRole
  | OrganizationRole
  | undefined;

export const isArgAuthenticatedUser = (
  arg: RoleAuthorizerPrimaryArg,
): arg is AuthenticatedUser | ClientAuthenticatedUser =>
  typeof arg === 'object' && 'selectedOrganizationRole' in arg;

export const isArgOrganizationRole = (
  arg: RoleAuthorizerPrimaryArg,
): arg is
  | ClientOrganizationRole
  | DistributedClientOrganizationRole
  | DistributedOrganizationRole
  | OrganizationRole => typeof arg === 'object' && 'role' in arg;

/**
 * An overloaded typeguard that returns whether or not the provided role, {@link AppRole},
 * organization role, {@link DistributedOrganizationRole}, or authenticated user,
 * {@link AuthenticatedUser}, is associated with a given {@link AppRole}, for both client-side
 * GraphQL and server-side Prisma representsations of the {@link AppRole} and
 * {@link OrganizationType}.
 */
export interface RoleAuthorizer<
  R extends AppRole = AppRole,
  G extends GraphQlAppRole = GraphQlAppRole,
> {
  (role: GraphQlAppRole | undefined): role is G;
  (role: AppRole | undefined): role is R;
  (user: AuthenticatedUser): user is AuthenticatedUserWithSelectedRole<R>;
  (orgRole: DistributedOrganizationRole): orgRole is DistributedOrganizationRole<R>;
  (orgRole: OrganizationRole): orgRole is OrganizationRole<R>;
  (orgRole: ClientOrganizationRole): orgRole is ClientOrganizationRole<G>;
  (orgRole: DistributedClientOrganizationRole): orgRole is DistributedClientOrganizationRole<G>;
  (user: ClientAuthenticatedUser): user is ClientAuthenticatedUserWithSelectedRole<G>;
}

export interface RoleAssertion<
  R extends AppRole = AppRole,
  G extends GraphQlAppRole = GraphQlAppRole,
> {
  (role: GraphQlAppRole | undefined): asserts role is G;
  (role: AppRole | undefined): asserts role is R;
  (user: AuthenticatedUser): asserts user is AuthenticatedUserWithSelectedRole<R>;
  (orgRole: DistributedOrganizationRole): asserts orgRole is DistributedOrganizationRole<R>;
  (orgRole: OrganizationRole): asserts orgRole is OrganizationRole<R>;
  (orgRole: ClientOrganizationRole): asserts orgRole is ClientOrganizationRole<G>;
  (
    orgRole: DistributedClientOrganizationRole,
  ): asserts orgRole is DistributedClientOrganizationRole<G>;
  (user: ClientAuthenticatedUser): asserts user is ClientAuthenticatedUserWithSelectedRole<G>;
}

/**
 * A form of the {@link RoleAuthorizer} that does not distinguish between client-side GraphQL and
 * server-side Prisma representsations of the {@link AppRole} and {@link OrganizationType}, which
 * should only ever be used for logic in this file that is calling a {@link RoleAuthorizer}
 * typeguard within the context of another overloaded function, such as {@link hasAnyRole} or
 * {@link assertHasAnyRole}.
 *
 * Coercion of a value of type {@link RoleAuthorizer} to this type is safe because the
 * implementation signature of {@link RoleAuthorizer} is guaranteed to be compatible with all of its
 * overload signatures.
 */
export interface ClientServerAgnosticRoleAuthorizer<
  R extends AppRole = AppRole,
  G extends GraphQlAppRole = GraphQlAppRole,
> {
  (role: AppRole | GraphQlAppRole | undefined): role is G | R;
  (
    user: AuthenticatedUser | ClientAuthenticatedUser,
  ): user is AuthenticatedUserWithSelectedRole<R> | ClientAuthenticatedUserWithSelectedRole<G>;
  (
    orgRole: DistributedClientOrganizationRole | DistributedOrganizationRole,
  ): orgRole is DistributedClientOrganizationRole<G> | DistributedOrganizationRole<R>;
  (
    orgRole: ClientOrganizationRole | OrganizationRole,
  ): orgRole is ClientOrganizationRole<G> | OrganizationRole<R>;
}

export type RoleAuthorizerTypeGuard<R extends AppRole, G extends GraphQlAppRole> = (
  role: AppRole | GraphQlAppRole,
) => role is G | R;

/**
 * An overloaded typeguard that returns whether or not the provided role and organization type,
 * {@link AppRole}/{@link OrganizationType}, organization role, {@link DistributedOrganizationRole},
 * or authenticated user, {@link AuthenticatedUser}, is associated with the {@link AppRole.ADMIN}
 * role and a given {@link OrganizationType}   for both client-side GraphQL and server-side Prisma
 * representsations of the {@link AppRole} and {@link OrganizationType}.
 */
export interface AdminRoleAuthorizer<
  O extends OrganizationType,
  GqlO extends GraphQlOrganizationType,
> {
  (
    role: GraphQlAppRole | undefined,
    orgType: GraphQlOrganizationType | undefined,
  ): role is GraphQlAppRole.Admin;
  (role: AppRole | undefined, orgType: OrganizationType | undefined): role is typeof AppRole.ADMIN;
  (user: AuthenticatedUser): user is AuthenticatedUserWithSelectedRole<typeof AppRole.ADMIN, O>;
  (
    orgRole: DistributedOrganizationRole,
  ): orgRole is DistributedOrganizationRole<typeof AppRole.ADMIN, O>;
  (orgRole: OrganizationRole): orgRole is OrganizationRole<typeof AppRole.ADMIN, O>;
  (orgRole: ClientOrganizationRole): orgRole is ClientOrganizationRole<GraphQlAppRole.Admin, GqlO>;
  (
    orgRole: DistributedClientOrganizationRole,
  ): orgRole is DistributedClientOrganizationRole<GraphQlAppRole.Admin, GqlO>;
  (
    user: ClientAuthenticatedUser,
  ): user is ClientAuthenticatedUserWithSelectedRole<GraphQlAppRole.Admin, GqlO>;
}

export type AdminRoleAuthorizerTypeGuard<
  O extends OrganizationType,
  GqlO extends GraphQlOrganizationType,
> = (
  role: AppRole | GraphQlAppRole,
  organizationType: GraphQlOrganizationType | OrganizationType,
) => organizationType is GqlO | O;
